<section class="portfolio-card" [class.strategic-only]="!showHeatmap" [attr.aria-busy]="loading">
  @if (showHeatmap) {
  <header class="portfolio-header">
    <div><h3>{{ data.heatmap.subtitle }}</h3><p>{{ data.heatmap.description }}</p></div>
    <button type="button" class="refresh" (click)="refresh()" [disabled]="loading" [attr.aria-label]="data.labels.refresh" [matTooltip]="data.labels.refresh"><mat-icon>refresh</mat-icon></button>
  </header>
  <div class="legends">
    <div class="legend">@if (data.labels.cellKey) { <strong>{{ data.labels.cellKey }}</strong> }
      @for (status of data.heatmap.statuses; track status.id) {
        <span><i class="swatch" [class.hatched]="status.pattern === 'hatched'" [style.background-color]="status.color"></i>{{ status.label }}</span>
      }
    </div>
    <div class="legend">@if (data.labels.iconKey) { <strong>{{ data.labels.iconKey }}</strong> }
      @for (icon of data.heatmap.icons; track icon.id) {
        <span><mat-icon [style.color]="icon.color">{{ icon.symbol }}</mat-icon>{{ icon.label }}</span>
      }
      @if (data.heatmap.iconDescription) { <small>{{ data.heatmap.iconDescription }}</small> }
    </div>
  </div>
  }
  @if (loading) { <p class="state" role="status">{{ data.labels.loading }}</p> }
  @else if (error) { <div class="state" role="alert"><p>{{ data.labels.error }}</p><button type="button" (click)="refresh()">{{ data.labels.retry }}</button></div> }
  @else {
    @if (!(showHeatmap ? hasData : strategic.projectCount)) { <p class="state" role="status">{{ data.labels.empty }}</p> }
    @if (showHeatmap) {
    <div class="matrix-scroll" tabindex="0" [attr.aria-label]="data.heatmap.subtitle">
      <table class="matrix" [style.min-width.px]="data.heatmap.minWidth">
        <caption class="sr-only">{{ data.heatmap.subtitle }}</caption>
        <thead><tr><th scope="col">{{ data.labels.market }}</th>
          @for (family of engineFamilies; track family.id) { <th scope="col">{{ family.label }}<small>{{ family.power }}</small></th> }
        </tr></thead>
        <tbody>
          @for (row of rows; track row.id) {
            <tr><th scope="row">{{ row.label }}</th>
              @for (cell of row.cells; track $index) {
                <td [style.background-color]="cell.status?.color || data.heatmap.emptyColor" [class.hatched]="cell.status?.pattern === 'hatched'" [matTooltip]="!cell.status && data.labels.unassignedStatusHint ? cell.label + '. ' + data.labels.unassignedStatusHint : ''">
                  <button type="button" class="cell" [disabled]="!cell.projects.length" [attr.aria-label]="!cell.status && data.labels.unassignedStatusHint ? cell.label + '. ' + data.labels.unassignedStatusHint : cell.label" [matTooltip]="cell.label" [matTooltipDisabled]="!cell.status && !!data.labels.unassignedStatusHint" (click)="openDetails(cell)">
                    <span class="cell-icons">@for (icon of cell.icons; track icon.id) { <mat-icon [style.color]="icon.color" [matTooltip]="icon.label">{{ icon.symbol }}</mat-icon> }</span>
                    @if (cell.note) { <span class="cell-note">{{ cell.note }}</span> }
                    @if (data.heatmap.showProjectCount && cell.projects.length) { <span class="cell-count">{{ cell.projects.length }} {{ data.labels.projects }}</span> }
                  </button>
                </td>
              }
            </tr>
          }
        </tbody>
      </table>
    </div>
    @if (data.labels.cellHint || (data.heatmap.showTotalProjectCount !== false && projectCount)) {
      <p class="matrix-note">{{ data.labels.cellHint }} @if (data.heatmap.showTotalProjectCount !== false && projectCount) { · {{ projectCount }} {{ data.labels.projects }} }</p>
    }
    }
    @if (data.strategicMap?.enabled) {
      <section class="strategic-section" [attr.aria-label]="data.strategicMap.title">
        <header class="strategic-header">
          <div><span class="section-eyebrow">{{ data.strategicMap.eyebrow }}</span><h3>{{ data.strategicMap.title }}</h3><p>{{ data.strategicMap.description }}</p></div>
          <div class="strategic-actions">
            <span class="project-total">{{ strategic.projectCount }} {{ data.labels.projects }}</span>
            @if (!showHeatmap) { <button type="button" class="refresh" (click)="refresh()" [disabled]="loading" [attr.aria-label]="data.labels.refresh" [matTooltip]="data.labels.refresh"><mat-icon>refresh</mat-icon></button> }
          </div>
        </header>
        <div class="strategy-scroll" tabindex="0" [attr.aria-label]="data.strategicMap.title">
          <table class="strategy-matrix" [style.min-width.px]="data.strategicMap.minWidth">
            <caption class="sr-only">{{ data.strategicMap.title }}</caption>
            <thead><tr><th scope="col">{{ data.strategicMap.labels.platform }}</th>
              @for (driver of data.strategicMap.drivers; track driver.id) {
                <th scope="col" [style.--driver-color]="driver.color"><mat-icon>{{ driver.icon }}</mat-icon><span>{{ driver.label }}</span><small>{{ driver.description }}</small></th>
              }
            </tr></thead>
            <tbody>
              @for (group of strategic.rows; track group.id) {
                <tr><th scope="row">{{ group.label }}<small>{{ group.description }}</small></th>
                  @for (cell of group.cells; track cell.id) {
                    <td [style.background-color]="cell.driver.background">
                      @if (!cell.needs.length) { <p class="no-needs">{{ data.strategicMap.labels.empty }}</p> }
                      @for (need of (expandedCells.has(cell.id) ? cell.needs : cell.needs.slice(0, data.strategicMap.visibleNeeds)); track need.title) {
                        <article class="need-card">
                          <span class="need-markets">{{ need.markets || data.strategicMap.labels.missingMarket }}</span>
                          <h4>{{ need.title }}</h4>
                          <button type="button" class="project-link" (click)="inspectNeed(need, group, cell.driver)">{{ need.projects.length }} {{ need.projects.length === 1 ? data.strategicMap.labels.linkedProject : data.strategicMap.labels.linkedProjects }}<mat-icon>arrow_forward</mat-icon></button>
                        </article>
                      }
                      @if (cell.needs.length > data.strategicMap.visibleNeeds) {
                        <button type="button" class="show-needs" [attr.aria-expanded]="expandedCells.has(cell.id)" (click)="toggleNeeds(cell.id)">{{ expandedCells.has(cell.id) ? data.strategicMap.labels.showLess : data.strategicMap.labels.showAll }}</button>
                      }
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
        <p class="strategy-note"><mat-icon>info_outline</mat-icon>{{ data.strategicMap.note }}</p>
        @if (strategic.unclassified) { <p class="classification-note">{{ strategic.unclassified }} {{ strategic.unclassified === 1 ? data.strategicMap.labels.unclassifiedSingle : data.strategicMap.labels.unclassified }}</p> }
      </section>
    }
  }
</section>

<ng-template #projectDetails let-selectedCell>
      <section class="details">
        <header><h4>{{ selectedCell.market }} / {{ selectedCell.family }}</h4><button type="button" (click)="closeDetails()" [attr.aria-label]="data.labels.close"><mat-icon>close</mat-icon></button></header>
        <div class="details-scroll"><table><thead><tr>@for (column of data.detailColumns; track column.field) { <th scope="col">{{ column.label }}</th> }</tr></thead>
          <tbody>@for (project of selectedCell.projects; track $index) { <tr>@for (column of data.detailColumns; track column.field) { <td>{{ project[column.field] ?? data.labels.noValue }}</td> }</tr> }</tbody>
        </table></div>
      </section>
</ng-template>
